import React, { useState, useEffect, useRef } from 'react'
import { VAPI_CONFIG } from '../../config'
import Vapi from '@vapi-ai/web'
import { supabase } from '../../lib/supabase'

export const AgentsPage = () => {
  const [assistants, setAssistants] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isVapiActive, setIsVapiActive] = useState(false)
  const [isStartingCall, setIsStartingCall] = useState(false)
  const [vapi, setVapi] = useState(null)
  const [selectedAssistant, setSelectedAssistant] = useState(null)
  const vapiRef = useRef(null)

  useEffect(() => {
    fetchAssistants()
    initializeVapi()
    return () => vapiRef.current?.stop()
  }, [])

  const initializeVapi = () => {
    try {
      if (!VAPI_CONFIG?.PUBLIC_KEY) {
        throw new Error('Vapi PUBLIC_KEY is not configured')
      }
      const vapiInstance = new Vapi(VAPI_CONFIG.PUBLIC_KEY)
      vapiRef.current = vapiInstance
      setVapi(vapiInstance)

      vapiInstance.on('call-start', () => {
        console.log('Call started')
        setIsVapiActive(true)
        setIsStartingCall(false)
        setError(null)
      })

      vapiInstance.on('call-end', () => {
        console.log('Call ended')
        setIsVapiActive(false)
      })

      vapiInstance.on('error', (e) => {
        console.error('Vapi error:', e)
        setError(e.message || 'An error occurred')
        setIsVapiActive(false)
        setIsStartingCall(false)
      })
    } catch (error) {
      console.error('Failed to initialize Vapi:', error)
      setError(error.message || 'Failed to initialize Vapi')
    }
  }

  const startVapiAssistant = async (assistantId) => {
    if (!vapi) {
      setError('Vapi not initialized')
      return
    }

    try {
      setIsStartingCall(true)
      setSelectedAssistant(assistantId)
      await vapi.start(assistantId)
    } catch (error) {
      console.error('Failed to start Vapi:', error)
      setError(error.message || 'Failed to start assistant')
      setIsVapiActive(false)
      setIsStartingCall(false)
    }
  }

  const endVapiCall = async () => {
    if (!vapi) {
      setError('Vapi not initialized')
      return
    }

    try {
      await vapi.stop()
      setIsVapiActive(false)
      setSelectedAssistant(null)
    } catch (error) {
      console.error('Failed to end Vapi call:', error)
      setError(error.message || 'Failed to end call')
    }
  }

  const fetchAssistants = async () => {
    try {
      setLoading(true)
      setError(null)
      setAssistants([]) // Reset assistants array

      // Get the current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        console.error('Supabase auth error:', userError);
        throw new Error(`Authentication error: ${userError.message}`);
      }
      if (!user) {
        throw new Error('Not authenticated. Please log in.');
      }

      // Get the user's organization memberships
      const { data: memberships, error: membershipsError } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id);

      if (membershipsError) {
        console.error('Supabase memberships error:', membershipsError);
        throw new Error(`Failed to fetch organization memberships: ${membershipsError.message}`);
      }
      
      if (!memberships || memberships.length === 0) {
        throw new Error('No organization found. Please contact your administrator.');
      }

      // Get all assistants for the organization
      const { data: assistantsData, error: assistantsError } = await supabase
        .from('organization_assistants')
        .select(`
          assistant_id,
          organizations (
            name
          )
        `)
        .eq('organization_id', memberships[0].organization_id);

      if (assistantsError) {
        console.error('Supabase assistants error:', assistantsError);
        throw new Error(`Failed to fetch assistants: ${assistantsError.message}`);
      }

      if (!assistantsData || assistantsData.length === 0) {
        throw new Error('No assistants found for your organization.');
      }

      // Fetch details for each assistant from Vapi
      const assistantsWithDetails = await Promise.all(
        assistantsData.map(async (assistant) => {
          try {
            const response = await fetch(`https://api.vapi.ai/assistant/${assistant.assistant_id}`, {
              headers: {
                'Authorization': `Bearer ${VAPI_CONFIG.SECRET_KEY}`,
                'Content-Type': 'application/json'
              }
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              console.error('Vapi API error:', errorData);
              throw new Error(`Failed to fetch details for assistant ${assistant.assistant_id}: ${errorData.message || response.statusText}`);
            }

            const details = await response.json();
            return {
              ...details,
              organizationName: assistant.organizations?.name || 'Unknown Organization'
            };
          } catch (error) {
            console.error(`Error fetching details for assistant ${assistant.assistant_id}:`, error);
            throw error;
          }
        })
      );

      setAssistants(assistantsWithDetails);
    } catch (err) {
      console.error('Error fetching assistants:', err);
      setError(err.message || 'Failed to load assistants');
      setAssistants([]); // Clear assistants on error
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#6C63FF]"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Agents</h1>
        <div className="bg-red-500/10 border border-red-500 text-red-500 rounded-lg p-4">
          {error}
        </div>
      </div>
    )
  }

  if (!assistants || assistants.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Agents</h1>
        <div className="bg-yellow-500/10 border border-yellow-500 text-yellow-500 rounded-lg p-4">
          No assistants found for your organization. Please contact your administrator to add assistants.
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Agents</h1>
      
      <div className="space-y-6">
        {assistants.map((assistant) => (
          <div key={assistant.id} className="bg-white dark:bg-[#1E1E2E] rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-semibold text-[#6C63FF]">{assistant.name || 'Unknown Assistant'}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">{assistant.organizationName}</p>
              </div>
              {vapi && (
                <button 
                  className={`px-4 py-2 rounded-md ${
                    isVapiActive && selectedAssistant === assistant.id
                      ? 'bg-red-500 hover:bg-red-600' 
                      : 'bg-[#6C63FF] hover:bg-[#4A45B2]'
                  } text-white transition-colors flex items-center gap-2`}
                  onClick={isVapiActive && selectedAssistant === assistant.id ? endVapiCall : () => startVapiAssistant(assistant.id)}
                  disabled={isStartingCall}
                >
                  {isStartingCall && selectedAssistant === assistant.id ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                      Starting...
                    </>
                  ) : isVapiActive && selectedAssistant === assistant.id ? (
                    'End Call'
                  ) : (
                    'Start Call'
                  )}
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-gray-50 dark:bg-[#2A2A3C] rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">First Message</h3>
                <p className="text-gray-600 dark:text-gray-400">{assistant.firstMessage || 'Not set'}</p>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-[#2A2A3C] rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">End Call Message</h3>
                <p className="text-gray-600 dark:text-gray-400">{assistant.endCallMessage || 'Not set'}</p>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-[#2A2A3C] rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Speaking Delay</h3>
                <p className="text-gray-600 dark:text-gray-400">{assistant.startSpeakingPlan?.waitSeconds?.toFixed(1) || '0.0'} seconds</p>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-[#2A2A3C] rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Voice Speed</h3>
                <p className="text-gray-600 dark:text-gray-400">{assistant.voice?.speed?.toFixed(1) || '1.0'}x</p>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-[#2A2A3C] rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Stop Speaking - Number of Words</h3>
                <p className="text-gray-600 dark:text-gray-400">{assistant.stopSpeakingPlan?.numWords || '0'} words</p>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-[#2A2A3C] rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Stop Speaking - Voice Seconds</h3>
                <p className="text-gray-600 dark:text-gray-400">{assistant.stopSpeakingPlan?.voiceSeconds?.toFixed(1) || '0.0'} seconds</p>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-[#2A2A3C] rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Background Noise Reduction</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {assistant.backgroundDenoisingEnabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
} 